<template>
    <template v-if="loading">
        <MiscSkeleton width="100%" height="400px" :repeat="limit ?? 12" />
    </template>
    <template v-else-if="listingItems.length > 0">
        <template v-for="(item, index) in listingItems" :key="index">
            <ProductListingCard
                v-if="item.type === 'product'"
                :id="`product-${(item.data as Schemas['Product']).id}`"
                :data="(item.data as Schemas['Product'])"
                :class="getKevelOrderClass(index)"
                :lazy-image="!(index === 0 && isFirstPage && !lcpHeroClaimed)"
                :preload-as-lcp="index === 0 && isFirstPage && !lcpHeroClaimed"
                :recede-on-scroll="true"
                @click="onListingCardClick((item.data as Schemas['Product']), index)"
                @keydown.up="onArrowNavigation('up', $event)"
                @keydown.right="onArrowNavigation('right', $event)"
                @keydown.down="onArrowNavigation('down', $event)"
                @keydown.left="onArrowNavigation('left', $event)"
            />
            <ProductListingTeaser
                v-if="item.type === 'teaser'"
                :data="(item.data as Schemas['A4gProductListingTeaser'])"
                :class="getKevelOrderClass(index)"
                @keydown.up="onArrowNavigation('up', $event)"
                @keydown.right="onArrowNavigation('right', $event)"
                @keydown.down="onArrowNavigation('down', $event)"
                @keydown.left="onArrowNavigation('left', $event)"
            />
        </template>
        <!-- Kevel plp-sponsored ad (reserves height even on no-fill, observed to trigger the fetch) -->
        <div
            v-if="showKevelSlot"
            :ref="props.kevelAdTarget"
            class="col-span-full"
        >
            <!-- Loading state -->
            <div
                v-if="props.kevelAdLoading"
                class="bg-base-200 animate-pulse"
                :class="kevelAdPlaceholderHeight"
            />
            <!-- Ad loaded -->
            <KevelAdSponsored
                v-else-if="props.kevelAd"
                :ad="props.kevelAd"
                :above-the-fold="false"
            />
            <!-- No-fill: reserved space -->
            <div
                v-else
                class="bg-base-200"
                :class="kevelAdPlaceholderHeight"
                aria-hidden="true"
            />
        </div>
    </template>
    <template v-else>
        {{ t('product.listing.noProductFound') }}
    </template>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { Schemas } from '#shopware'
import { useI18n } from 'vue-i18n'
import { onArrowNavigation } from '~/utils/grid-arrow-navigation'
import { getTranslatedProperty } from '@shopware/helpers'

const { t } = useI18n()

const props = defineProps<{
    products: Schemas['Product'][],
    teaser?: Schemas['A4gProductListingTeaser'][],
    page?: number,
    limit?: number,
    loading?: boolean,
    kevelAd?: ParsedKevelAd | null,
    kevelAdLoading?: boolean,
    // Function ref for the ad slot; passing it enables the plp-sponsored slot (page 1 only)
    kevelAdTarget?: (el: Element | ComponentPublicInstance | null) => void,
}>()

const listingItems = computed(() => {
    const products = props.products.map((product, index) => {
        return {
            pos: index,
            type: 'product' as const,
            data: product
        }
    })

    const teaserResponse = (props.teaser != null
        ? Object.values(props.teaser)
        : []) as (Schemas['A4gProductListingTeaser'] | string)[];

    const teaser: (Schemas['A4gProductListingTeaser'] & { pos: number; type: string; data: Schemas['A4gProductListingTeaser'] })[] = [];

    teaserResponse.forEach(t => {
        // 2. Use a 'Type Guard' to check if t is an object with the property we need
        if (typeof t === 'object' && t !== null && 'position' in t) {
            teaser.push({
                ...t,
                pos: t.position - 1,
                type: 'teaser',
                data: t
            });
        }
    });

    // teaser first to respect teaser position, then products
    const items = [...teaser, ...products]
    items.sort((a, b) => a?.pos - b?.pos)

    return items
})

const nuxtApp = useNuxtApp()
const { category } = useCategory()
const route = useRoute()
const { storeProductNavigation } = useProductNavigation()
const runtimeConfig = useRuntimeConfig()

const isFirstPage = computed(() => !props.page || props.page === 1)

// Kevel ad slot (first page only) is rendered after all items in the DOM and pulled into
// place via CSS order, so its position follows the grid's breakpoints without JS
// (no SSR/client mismatch, no recalculation on resize). It is rendered before the decision
// arrives so it can serve as the intersection observer target and reserve its space.
const showKevelSlot = computed(() => props.kevelAdTarget != null && isFirstPage.value)
const kevelAdRowsBefore = Math.max(0, (Number(runtimeConfig.public.kevelPlpSponsoredRow) || 2) - 1)
// Placeholder height matches the rendered ad as measured on stage (375 / 768 / 1280px)
const kevelAdPlaceholderHeight = 'min-h-[521px] md:min-h-[634px] xl:min-h-[594px]'

/**
 * Items before the ad keep the default order 0 and precede the ad (also order 0, but last in DOM);
 * items after it get order-1. Grid columns: 2 (mobile), 3 (lg), 4 (xl/2xl) — see gridClasses in ProductListing.
 */
function getKevelOrderClass (index: number): string | undefined {
    if (!showKevelSlot.value) return undefined
    if (index < 2 * kevelAdRowsBefore) return undefined
    if (index < 3 * kevelAdRowsBefore) return 'order-1 lg:order-none'
    if (index < 4 * kevelAdRowsBefore) return 'order-1 xl:order-none'
    return 'order-1'
}
const lcpHeroClaimed = useState<boolean>('a4g-lcp-hero-claimed', () => false)

function onListingCardClick (product: Schemas['Product'], index: number) {
    const itemListContextStore = useItemListContextStore()

    // Calculate absolute position: (page - 1) * limit + index
    const page = props.page || 1
    const limit = props.limit || 24
    const absoluteIndex = (page - 1) * limit + index

    // Store list context for tracking
    itemListContextStore.setItemListContextData({
        item_list_id: category?.value?.id || 'listing',
        item_list_name: getTranslatedProperty(category.value, 'name') || 'Product Listing',
        index: absoluteIndex
    })

    // Store navigation data before navigating to product detail
    if (props.page) {
        storeProductNavigation(
            product.id.toString(),
            props.page,
            route.path
        )
    }

    nuxtApp.callHook('clickProductListingCard', { id: category?.value?.id, name: getTranslatedProperty(category.value, 'name'), product, index: absoluteIndex })
}
</script>
